import Joyride, { Placement, ACTIONS, EVENTS, STATUS, CallBackProps, Step } from 'react-joyride'
import { Box, Button, Modal, Stack, Typography, useTheme } from '@mui/material'
import PanToolAltIcon from '@mui/icons-material/TouchApp'
import { useTranslation } from 'react-i18next'
import { InfoOutlined, HelpRounded } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  changeWalkthroughIndex,
  changeWalkthroughStatus,
  changeWalkthroughVideo,
} from '../../../store/actions'
import { WALKTHROUGH_STATUS } from '../../../shared/constants'
import { ReactComponent as WelcomeLogo } from '../../../assets/walkthrough/welcome.svg'
import { ReactComponent as CongratsLogo } from '../../../assets/walkthrough/congrats.svg'
import FsDashPlayer from './DashPlayer'

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
}

const FsWalkthrough = () => {
  const { walkthroughStatus, walkthroughIndex, isAuthenticated, isWalkthroughVideo } =
    useAppSelector((state) => state.currentUser)
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const theme = useTheme()

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { action, index, status, type } = data

    if (action === ACTIONS.CLOSE) {
      dispatch(changeWalkthroughStatus(WALKTHROUGH_STATUS.Skipped))
    } else if (status === STATUS.SKIPPED) {
      dispatch(changeWalkthroughStatus(WALKTHROUGH_STATUS.Skipped))
    } else if (status === STATUS.FINISHED) {
      dispatch(changeWalkthroughStatus(WALKTHROUGH_STATUS.Done))
      dispatch(changeWalkthroughIndex(0))
    } else if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      dispatch(changeWalkthroughStatus(WALKTHROUGH_STATUS.InProgress))
      dispatch(changeWalkthroughIndex(index + (action === ACTIONS.PREV ? -1 : 1)))
    }
  }

  const totalSteps = 12

  const WalkthroughSmall = (stepIndex = 0, title = '', content: any = '', width = 400) => (
    <Box textAlign="left">
      <Box px={2} pt={1} pb={0.5} sx={{ borderBottom: `2px solid ${theme.palette.primary.light}` }}>
        <Typography variant="caption" color="primary.main">
          {stepIndex}/{totalSteps}
        </Typography>
        <Typography fontWeight="bold" color="primary.main">
          {title}
        </Typography>
      </Box>
      <Box px={2} sx={{ width: `${width}px` }}>
        <Typography mt={1}>{content}</Typography>
      </Box>
    </Box>
  )

  const steps: Step[] = [
    {
      target: 'body',
      placement: 'center' as 'center' | Placement | 'auto' | undefined,
      content: (
        <Box pt={4} width="550px">
          <WelcomeLogo color={theme.palette.primary.main} />
          <Typography variant="h6" fontWeight="bold" color="primary.main">
            {t('walkthrough.1')}
          </Typography>
          <Typography mt={2} mx={4}>
            {t('walkthrough.2')}
          </Typography>
          <Typography mx={4}>{t('walkthrough.3')}</Typography>
          <Typography mt={2} mx={6} color="primary.main">
            {t('walkthrough.4')} <HelpRounded fontSize="inherit" sx={{ mb: -0.2 }} />{' '}
            {t('walkthrough.5')}
          </Typography>
        </Box>
      ),
    },
    {
      target: '.maindate',
      content: WalkthroughSmall(1, t('walkthrough.6'), t('walkthrough.7')),
    },
    {
      target: '.walkthroughmodule0',
      content: WalkthroughSmall(2, t('walkthrough.8'), t('walkthrough.9')),
      disableScrolling: true,
      placement: 'auto',
    },
    {
      target: '.walkthroughdivclass',
      content: WalkthroughSmall(
        3,
        t('walkthrough.10'),
        <>
          <Typography>{t('walkthrough.11')}</Typography>
          <Stack mt={2} direction="row">
            <PanToolAltIcon fontSize="small" sx={{ color: 'primary.main' }} />
            <Typography ml={1}>{t('overview.bar.chart.description')}</Typography>
          </Stack>
        </>,
      ),
      placement: 'right',
      disableScrolling: true,
      spotlightClicks: true,
    },
    {
      target: '.status_walkthrough',
      content: WalkthroughSmall(4, t('walkthrough.13'), t('walkthrough.14')),
      placement: 'right',
      hideBackButton: true,
    },
    {
      target: '.gaps_walkthrough',
      content: WalkthroughSmall(
        5,
        t('walkthrough.43'),
        <Box>
          <Typography>{t('walkthrough.44')}</Typography>
          <Stack mt={2} direction="row">
            <PanToolAltIcon fontSize="small" sx={{ color: 'primary.main' }} />
            <Typography ml={1}>
              {t('walkthrough.15')}{' '}
              <InfoOutlined sx={{ color: 'primary.main', mb: -0.5 }} fontSize="small" />{' '}
              {t('walkthrough.16')}
            </Typography>
          </Stack>
        </Box>,
      ),
      disableScrolling: true,
      spotlightClicks: true,
      placement: 'right',
    },
    {
      target: '.causes_walkthrough',
      content: WalkthroughSmall(
        6,
        t('walkthrough.18'),
        <Typography>{t('walkthrough.19')}</Typography>,
      ),
      placement: 'left',
    },
    {
      target: '.gap_1_walkthrough',
      content: WalkthroughSmall(
        7,
        t('walkthrough.25'),
        <Typography>{`${t('walkthrough.26')} ${t('walkthrough.45')}`}</Typography>,
      ),
    },
    {
      target: '.gap_2_walkthrough',
      content: WalkthroughSmall(
        8,
        t('walkthrough.27'),
        <Typography>{t('walkthrough.28')}</Typography>,
      ),
    },
    {
      target: '.reporticon',
      content: WalkthroughSmall(
        9,
        t('walkthrough.29'),
        <Box>
          <Typography>{t('walkthrough.30')}</Typography>
          <Stack mt={2} direction="row">
            <PanToolAltIcon sx={{ color: 'primary.main' }} />
            <Typography ml={1}>{t('walkthrough.31')}</Typography>
          </Stack>
        </Box>,
      ),
      disableScrolling: true,
      spotlightClicks: true,
    },
    {
      target: '.createreport',
      content: WalkthroughSmall(
        10,
        t('walkthrough.32'),
        <Typography>{t('walkthrough.33')}</Typography>,
      ),
      disableScrolling: true,
      hideBackButton: true,
    },
    {
      target: '.etlicon',
      content: WalkthroughSmall(
        11,
        t('walkthrough.41'),
        <Typography>{t('walkthrough.42')}</Typography>,
      ),
      disableScrolling: true,
    },
    {
      target: '.help_walkthrough',
      content: WalkthroughSmall(
        12,
        t('walkthrough.34'),
        <Typography>{t('walkthrough.35')}</Typography>,
      ),
      disableScrolling: true,
    },
    {
      target: 'body',
      placement: 'center' as 'center' | Placement | 'auto' | undefined,
      content: (
        <Box py={4} width="550px">
          <CongratsLogo color={theme.palette.primary.main} />
          <Typography variant="h6" fontWeight="bold" color="primary.main">
            {t('walkthrough.36')}
          </Typography>
          <Typography mt={2} mx={4}>
            {t('walkthrough.37')}
          </Typography>
          <Typography mx={4}>{t('walkthrough.38')}</Typography>
          <Stack direction="row" alignItems="center" justifyContent="space-around" mt={2}>
            <Button
              variant="outlined"
              sx={{
                borderRadius: '4px',
                padding: '8px 16px',
              }}
              onClick={() => {
                dispatch(changeWalkthroughStatus(WALKTHROUGH_STATUS.Done))
                dispatch(changeWalkthroughVideo(true))
              }}
            >
              {t('walkthrough.video')}
            </Button>
            <Button
              variant="contained"
              sx={{
                borderRadius: '4px',
                padding: '8px 16px',
              }}
              onClick={() => dispatch(changeWalkthroughStatus(WALKTHROUGH_STATUS.Done))}
            >
              {t('walkthrough.done')}
            </Button>
          </Stack>
        </Box>
      ),
      hideFooter: true,
    },
  ]

  const handleClose = () => {
    dispatch(changeWalkthroughVideo(false))
  }

  return (
    <>
      <Joyride
        steps={steps}
        run={walkthroughStatus === WALKTHROUGH_STATUS.InProgress && isAuthenticated}
        disableCloseOnEsc
        stepIndex={walkthroughIndex}
        callback={handleJoyrideCallback}
        continuous
        showSkipButton
        disableOverlayClose
        styles={{
          overlay: { height: '100%' },
          tooltip: {
            minWidth: '400px',
            padding: '0px',
            width: 'fit-content',
          },
          tooltipContent: { padding: '0px' },
          buttonNext: {
            fontSize: '14px',
            padding: '10px 16px',
            margin: '16px',
            color: theme.palette.background.paper,
          },
          buttonBack: {
            fontSize: '14px',
            padding: '8px',
          },
          buttonSkip: {
            fontSize: '14px',
            padding: '8px 16px',
            margin: '16px',
            color: theme.palette.primary.main,
            border: `1px solid ${theme.palette.primary.main}`,
            borderRadius: '4px',
          },
          options: {
            zIndex: 2005,
            primaryColor: theme.palette.primary.main,
            backgroundColor: theme.palette.background.paper,
            arrowColor: theme.palette.background.paper,
            textColor: theme.palette.text.primary,
            overlayColor: theme.palette.divider,
          },
        }}
        locale={{
          back: t('walkthrough.back'),
          skip: t('walkthrough.skip'),
          next: t('walkthrough.next'),
        }}
      />
      <Modal
        open={isWalkthroughVideo}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography id="modal-modal-title" variant="h6" component="h2">
              {t('video.tutorial')}
            </Typography>
            <Box onClick={handleClose} sx={{ cursor: 'pointer' }}>
              <CloseIcon />
            </Box>
          </Stack>
          <FsDashPlayer />
        </Box>
      </Modal>
    </>
  )
}

export default FsWalkthrough
